import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCheck } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import type { TicketDetail } from '@aurora/contracts';
import { post, type ApiError } from '../lib/api';
import { textToHtml } from '../lib/format';
import { useSession } from '../lib/session';
import { useGroups, usePriorities } from '../lib/directory';
import { PersonPicker, type PersonOption } from '../components/person-picker';
import { useToast } from '../components/toast';
import { Button, Card, Field, Input, Select, Textarea } from '../components/ui';
import { PageHeader } from './shell';
import { AttachmentPicker, useAttachmentUpload } from '../features/tickets/attachments';
import {
  CustomFieldInputs,
  seedCustomFields,
  unansweredRequired,
  useTicketFormFields,
} from '../features/tickets/custom-fields';
import { useTicketChoices } from '../features/tickets/detail-data';
import { DEFAULT_LEVEL, LEVEL_CHOICES, matrixPriority } from '../features/tickets/priority-matrix';

/**
 * Raising a ticket.
 *
 * Two forms, chosen by who is asking. An agent logs a call or a walk-up for
 * somebody else: who it is for comes first, then what happened, then — all
 * optional — how it should be filed and who should take it. A requester
 * describes their problem and attaches a screenshot; the desk decides the
 * rest, and the server refuses those fields from them regardless.
 *
 * Only the subject is required. Demanding a classification up front is how a
 * desk ends up with a wall of "Other / Other / Medium" nobody trusts.
 */

/** The fields this form draws, so a refusal about anything else is still shown. */
const PLACED = new Set([
  'subject',
  'description_text',
  'requester_id',
  'priority',
  'urgency',
  'impact',
  'type',
  'source',
  'group_id',
  'assignee_id',
  'tags',
  'attachment_ids',
]);

function isPlaced(path: string): boolean {
  return PLACED.has(path) || path.startsWith('custom_fields.') || path.startsWith('tags.');
}

/**
 * Channels only a machine uses. An agent logging a call was offered "API",
 * "Automation" and "Monitoring alert" beside Phone and Walk-up; those are set by
 * the integrations that raise such tickets, never by a person at this form.
 */
const MACHINE_SOURCES = new Set(['api', 'automation', 'alert']);

/**
 * What was typed, kept for this tab until the ticket is created or the form is
 * cancelled.
 *
 * Following any link out of the form — the sidebar, the search, a
 * notification — threw away a half-logged phone call, and the router this app
 * uses cannot hold a navigation to ask first. So the words are kept instead,
 * and are there again on coming back. Keyed by the person, so whoever signs in
 * next in the same tab does not inherit them.
 */
function useStoredText(key: string | null): [string, (value: string) => void] {
  const [value, setValue] = useState('');
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  if (key !== loadedFor) {
    setLoadedFor(key);
    let stored = '';
    try {
      stored = key ? (window.sessionStorage.getItem(key) ?? '') : '';
    } catch {
      // Storage can be unavailable (private windows, blocked site data).
    }
    setValue(stored);
  }
  const update = (next: string) => {
    setValue(next);
    if (!key) return;
    try {
      if (next) window.sessionStorage.setItem(key, next);
      else window.sessionStorage.removeItem(key);
    } catch {
      // Kept in memory for as long as the page is open.
    }
  };
  return [value, update];
}

export function NewTicketRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { session, can } = useSession();
  const isAgent = Boolean(session?.is_agent);
  const mayRaiseForOthers = isAgent && (can('catalog.request_for_others') || can('ticket.edit_requester'));
  const mayAssign = isAgent && can('ticket.assign');

  const ids = {
    requester: useId(),
    subject: useId(),
    description: useId(),
    source: useId(),
    type: useId(),
    priority: useId(),
    urgency: useId(),
    impact: useId(),
    group: useId(),
    assignee: useId(),
    tags: useId(),
  };

  const formFields = useTicketFormFields();
  const meta = formFields.data?.meta;
  const definitions = formFields.data?.data ?? [];
  const showPriority = isAgent || meta?.allow_requester_priority === true;
  const priorities = usePriorities(showPriority);
  const groups = useGroups(isAgent);
  const choices = useTicketChoices(isAgent);
  const upload = useAttachmentUpload();

  const draftKey = session ? `aurora:new-ticket:${session.user.id}` : null;
  const [requester, setRequester] = useState<PersonOption | null>(null);
  const [subject, setSubject] = useStoredText(draftKey && `${draftKey}:subject`);
  const [description, setDescription] = useStoredText(draftKey && `${draftKey}:description`);
  const [source, setSource] = useState('phone');
  const [type, setType] = useState('');
  const [priority, setPriority] = useState('');
  /** Whether the agent picked the priority themselves, which then wins over urgency and impact. */
  const [priorityChosen, setPriorityChosen] = useState(false);
  const [urgency, setUrgency] = useState('');
  const [impact, setImpact] = useState('');
  const [groupId, setGroupId] = useState('');
  const [assignee, setAssignee] = useState<PersonOption | null>(null);
  const [tags, setTags] = useState('');
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [seeded, setSeeded] = useState(false);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  /**
   * Urgency or impact given and no priority picked by hand: the priority the
   * desk's grid gives the pair — the one the server will give the ticket, so
   * the form shows it and leaves the priority out of what it sends. Whichever
   * of the two is not set counts as medium, as the server counts it.
   */
  const matrixOn = isAgent && meta?.priority_matrix?.enabled === true;
  const levelsSet = urgency !== '' || impact !== '';
  const followedPriority =
    matrixOn && levelsSet && !priorityChosen
      ? matrixPriority(meta?.priority_matrix, Number(urgency || DEFAULT_LEVEL), Number(impact || DEFAULT_LEVEL))
      : null;

  // "Me" until somebody else is chosen.
  useEffect(() => {
    if (session && requester === null && mayRaiseForOthers) {
      setRequester({ id: session.user.id, name: session.user.name, email: session.user.email });
    }
  }, [session, requester, mayRaiseForOthers]);

  if (!seeded && formFields.data) {
    // Defaults are what to use in the absence of an answer.
    setCustomFields(seedCustomFields(definitions));
    if (meta?.default_priority) setPriority(meta.default_priority);
    setSeeded(true);
  }

  useEffect(() => {
    const previous = document.title;
    document.title = `${isAgent ? 'New ticket' : 'Report an issue'} · Aurora`;
    return () => {
      document.title = previous;
    };
  }, [isAgent]);

  const typed = subject.trim() !== '' || description.trim() !== '' || upload.pending.length > 0 || upload.busy;
  useEffect(() => {
    if (!typed) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [typed]);

  const reset = () => {
    setSubject('');
    setDescription('');
    setTags('');
    setAssignee(null);
    setCustomFields(seedCustomFields(definitions));
    setLocalErrors({});
    upload.clear();
    if (session && mayRaiseForOthers) {
      setRequester({ id: session.user.id, name: session.user.name, email: session.user.email });
    }
  };

  const create = useMutation<{ data: TicketDetail }, ApiError, { another: boolean }>({
    mutationFn: () => {
      const tagList = tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
      const attachmentIds = upload.ids();
      return post<{ data: TicketDetail }>('/tickets', {
        subject: subject.trim(),
        description_text: description,
        description_html: textToHtml(description),
        ...(mayRaiseForOthers && requester ? { requester_id: requester.id } : {}),
        // Left out while it follows urgency and impact: the server works it
        // out from the same grid, and an explicit priority would win over it.
        ...(showPriority && priority && !followedPriority ? { priority } : {}),
        ...(isAgent && urgency ? { urgency: Number(urgency) } : {}),
        ...(isAgent && impact ? { impact: Number(impact) } : {}),
        ...(isAgent && source ? { source } : {}),
        ...(isAgent && type ? { type } : {}),
        ...(isAgent && groupId ? { group_id: Number(groupId) } : {}),
        ...(mayAssign && assignee ? { assignee_id: assignee.id } : {}),
        ...(isAgent && tagList.length ? { tags: tagList } : {}),
        ...(Object.keys(customFields).length > 0 ? { custom_fields: customFields } : {}),
        ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
      });
    },
    onSuccess: async (result, { another }) => {
      // The list was cached for fifteen seconds and did not know about it.
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['requester-home'] });
      toast.success(`Ticket #${result.data.number} created`, another ? 'The form is ready for the next one.' : undefined);
      if (another) {
        reset();
        document.getElementById(ids.subject)?.focus();
        return;
      }
      // Sent: nothing is left to come back to.
      setSubject('');
      setDescription('');
      await navigate(`/tickets/${result.data.number}`, { replace: true });
    },
  });

  const submit = (another = false) => {
    const problems: Record<string, string> = {};
    const tooLong = tags
      .split(',')
      .map((tag) => tag.trim())
      .find((tag) => tag.length > 64);
    if (tooLong) problems.tags = `“${tooLong.slice(0, 20)}…” is longer than 64 characters.`;
    setLocalErrors(problems);
    if (Object.keys(problems).length > 0) return;
    create.mutate({ another });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  // Enter in a one-line box moves on rather than raising a half-written
  // ticket; Ctrl+Enter anywhere is the deliberate way to send it.
  const onKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    const target = event.target as HTMLElement;
    if (event.key !== 'Enter') return;
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      if (canSubmit) submit();
      return;
    }
    if (target instanceof HTMLInputElement && target.type !== 'checkbox' && target.getAttribute('role') !== 'combobox') {
      event.preventDefault();
      if (target.id === ids.subject) document.getElementById(ids.description)?.focus();
    }
  };

  const serverErrors = create.error?.fieldErrors ?? {};
  const errors = { ...serverErrors, ...localErrors };
  const tagError = errors.tags ?? Object.entries(errors).find(([path]) => path.startsWith('tags.'))?.[1];
  // A refusal about something this form does not draw is still a refusal.
  const unplaced = create.error
    ? (create.error.details ?? []).filter((detail) => !isPlaced(detail.path)).map((detail) => detail.message)
    : [];
  const generalError =
    create.error && (!create.error.details || create.error.details.length === 0) ? create.error.message : null;

  const missing = unansweredRequired(definitions, customFields);
  const canSubmit = subject.trim() !== '' && missing.length === 0 && !upload.busy && !create.isPending;
  const sources = (choices.data?.source ?? []).filter((choice) => !MACHINE_SOURCES.has(choice.value));

  const describedBy = (id: string, error?: string) =>
    error ? { 'aria-invalid': true, 'aria-describedby': `${id}-error` } : {};

  // Which form this is depends on who is asking; drawing the requester's and
  // then swapping in the agent's is a flash of the wrong form.
  if (!session) {
    return (
      <div className="flex h-full flex-col" aria-busy aria-label="Loading">
        <PageHeader title="New ticket" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={isAgent ? 'New ticket' : 'Report an issue'}
        description={
          isAgent
            ? mayRaiseForOthers
              ? 'Log a call, an email or a walk-up for anybody, or raise one for yourself.'
              : 'Raise a ticket for yourself.'
            : 'Tell us what is wrong and we will get back to you.'
        }
        back={{ to: '/tickets', label: isAgent ? 'Tickets' : 'My requests' }}
      />

      <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <form onSubmit={handleSubmit} onKeyDown={onKeyDown} noValidate className="mx-auto max-w-3xl space-y-3">
          {(generalError || unplaced.length > 0) && (
            <div
              role="alert"
              className="rounded-control border border-critical-line bg-critical-soft px-3 py-2 text-body text-critical-ink"
            >
              {generalError ?? (
                <ul className="list-inside list-disc space-y-0.5">
                  {unplaced.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <Card className="space-y-4 p-4">
            {mayRaiseForOthers && (
              <Field label="Requester" htmlFor={ids.requester} error={errors.requester_id} required>
                <PersonPicker
                  id={ids.requester}
                  source="users"
                  value={requester}
                  onChange={(person) => setRequester(person)}
                  placeholder="Who is this for? Search by name or email…"
                  aria-invalid={Boolean(errors.requester_id)}
                  {...(errors.requester_id ? { 'aria-describedby': `${ids.requester}-error` } : {})}
                />
              </Field>
            )}

            <Field label="Subject" htmlFor={ids.subject} error={errors.subject} required>
              <Input
                id={ids.subject}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder={isAgent ? 'Briefly, what is happening?' : 'In a few words, what is wrong?'}
                autoFocus
                required
                maxLength={500}
                {...describedBy(ids.subject, errors.subject)}
              />
            </Field>

            <Field
              label="Description"
              htmlFor={ids.description}
              error={errors.description_text}
              hint="What were you doing, what did you expect, and what happened instead?"
            >
              <Textarea
                id={ids.description}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="min-h-40 text-reading"
                {...describedBy(ids.description, errors.description_text)}
              />
            </Field>

            <div>
              <AttachmentPicker upload={upload} disabled={create.isPending} label="Attach files" />
              {errors.attachment_ids && (
                <p role="alert" className="mt-1 text-mini text-critical-ink">
                  {errors.attachment_ids}
                </p>
              )}
            </div>
          </Card>

          {(isAgent || showPriority) && (
            <Card className="space-y-4 p-4">
              <div>
                <h2 className="text-reading font-semibold text-ink">{isAgent ? 'Classification' : 'Priority'}</h2>
                {isAgent && (
                  <p className="text-mini text-ink-muted">All optional. Leave anything you are unsure of for triage.</p>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {isAgent && (
                  <Field label="Source" htmlFor={ids.source} error={errors.source}>
                    <Select
                      id={ids.source}
                      value={source}
                      onChange={(event) => setSource(event.target.value)}
                      {...describedBy(ids.source, errors.source)}
                    >
                      {sources.length === 0 && <option value="phone">Phone</option>}
                      {sources.map((choice) => (
                        <option key={choice.value} value={choice.value}>
                          {choice.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {isAgent && (
                  <Field label="Type" htmlFor={ids.type} error={errors.type}>
                    <Select
                      id={ids.type}
                      value={type}
                      onChange={(event) => setType(event.target.value)}
                      {...describedBy(ids.type, errors.type)}
                    >
                      <option value="">Decide later</option>
                      {(choices.data?.type ?? []).map((choice) => (
                        <option key={choice.value} value={choice.value}>
                          {choice.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {isAgent && (
                  <Field label="Urgency" htmlFor={ids.urgency} error={errors.urgency} hint="How soon it needs fixing.">
                    <Select
                      id={ids.urgency}
                      value={urgency}
                      onChange={(event) => setUrgency(event.target.value)}
                      {...describedBy(ids.urgency, errors.urgency)}
                    >
                      <option value="">Not set</option>
                      {LEVEL_CHOICES.map((choice) => (
                        <option key={choice.value} value={choice.value}>
                          {choice.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {isAgent && (
                  <Field label="Impact" htmlFor={ids.impact} error={errors.impact} hint="How widely it is felt.">
                    <Select
                      id={ids.impact}
                      value={impact}
                      onChange={(event) => setImpact(event.target.value)}
                      {...describedBy(ids.impact, errors.impact)}
                    >
                      <option value="">Not set</option>
                      {LEVEL_CHOICES.map((choice) => (
                        <option key={choice.value} value={choice.value}>
                          {choice.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {showPriority && (
                  <Field label="Priority" htmlFor={ids.priority} error={errors.priority}>
                    <Select
                      id={ids.priority}
                      value={followedPriority ?? priority}
                      onChange={(event) => {
                        setPriority(event.target.value);
                        setPriorityChosen(true);
                      }}
                      {...(errors.priority
                        ? describedBy(ids.priority, errors.priority)
                        : matrixOn && levelsSet
                          ? { 'aria-describedby': `${ids.priority}-follows` }
                          : {})}
                    >
                      {!priority && !followedPriority && <option value="">Default</option>}
                      {(priorities.data ?? []).map((choice) => (
                        <option key={choice.key} value={choice.key}>
                          {choice.name}
                        </option>
                      ))}
                    </Select>
                    {matrixOn && levelsSet && !errors.priority && (
                      <p id={`${ids.priority}-follows`} className="text-mini text-ink-subtle">
                        {priorityChosen ? (
                          <>
                            You chose this, so urgency and impact will not change it.{' '}
                            <button
                              type="button"
                              onClick={() => setPriorityChosen(false)}
                              className="font-medium text-accent-ink hover:underline"
                            >
                              Follow urgency and impact
                            </button>
                          </>
                        ) : followedPriority ? (
                          'Follows urgency and impact. Choose one to set it yourself.'
                        ) : (
                          'Urgency and impact give no priority of their own here, so this one is kept.'
                        )}
                      </p>
                    )}
                  </Field>
                )}
                {isAgent && (
                  <Field label="Group" htmlFor={ids.group} error={errors.group_id}>
                    <Select
                      id={ids.group}
                      value={groupId}
                      onChange={(event) => {
                        setGroupId(event.target.value);
                        setAssignee(null);
                      }}
                      {...describedBy(ids.group, errors.group_id)}
                    >
                      <option value="">Let the rules decide</option>
                      {(groups.data ?? []).map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {mayAssign && (
                  <Field label="Assignee" htmlFor={ids.assignee} error={errors.assignee_id}>
                    <div className="space-y-1">
                      <PersonPicker
                        id={ids.assignee}
                        source="agents"
                        value={assignee}
                        onChange={setAssignee}
                        groupId={groupId ? Number(groupId) : null}
                        clearable
                        clearLabel="Nobody yet"
                        placeholder="Leave for the queue, or pick an agent…"
                        aria-invalid={Boolean(errors.assignee_id)}
                      />
                      {assignee?.id !== session.user.id && (
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setAssignee({ id: session.user.id, name: session.user.name })}
                        >
                          <UserCheck aria-hidden className="size-3.5" />
                          Assign to me
                        </Button>
                      )}
                    </div>
                  </Field>
                )}
                {isAgent && (
                  <Field label="Tags" htmlFor={ids.tags} error={tagError} hint="Separate tags with commas.">
                    <Input
                      id={ids.tags}
                      value={tags}
                      onChange={(event) => setTags(event.target.value)}
                      placeholder="network, vpn"
                      {...describedBy(ids.tags, tagError)}
                    />
                  </Field>
                )}
              </div>
            </Card>
          )}

          {definitions.length > 0 && (
            <Card className="space-y-4 p-4">
              <CustomFieldInputs
                fields={definitions}
                values={customFields}
                errors={errors}
                onChange={(key, value) => setCustomFields((current) => ({ ...current, [key]: value }))}
              />
            </Card>
          )}

          {!isAgent && can('catalog.view') && (
            <p className="text-body text-ink-muted">
              Need something new rather than reporting a problem — a laptop, access, software?{' '}
              <Link to="/catalog" className="font-medium text-accent-ink hover:underline">
                Request it from the service catalog
              </Link>
              .
            </p>
          )}

          {missing.length > 0 && <p className="text-body text-caution-ink">Still needed: {missing.join(', ')}.</p>}

          <div className="flex flex-wrap items-center justify-end gap-2 pb-4">
            <span className="me-auto hidden text-mini text-ink-subtle sm:inline">
              {isAgent ? 'Ctrl+Enter creates the ticket.' : 'Ctrl+Enter sends it.'}
            </span>
            <Button variant="ghost" asChild>
              {/* Cancelling is deliberate, so what was typed goes with it. */}
              <Link
                to="/tickets"
                onClick={() => {
                  setSubject('');
                  setDescription('');
                }}
              >
                Cancel
              </Link>
            </Button>
            {isAgent && (
              <Button
                disabled={!canSubmit}
                onClick={() => submit(true)}
                loading={create.isPending && create.variables?.another}
              >
                Create and add another
              </Button>
            )}
            <Button
              type="submit"
              variant="primary"
              loading={create.isPending && !create.variables?.another}
              disabled={!canSubmit}
            >
              {upload.busy ? 'Uploading…' : isAgent ? 'Create ticket' : 'Send'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
